import { useQuery } from '@apollo/react-hooks'
import { Card, DialogContent, makeStyles, useTheme } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents'
import ExpandMore from '@material-ui/icons/ExpandMore'
import Help from '@material-ui/icons/Help'
import { formatCurrency, getObjectLabels } from '@paintscout/util/builder'
import { gql } from 'apollo-boost'
import classNames from 'classnames'
import moment from 'moment'
import React, { useCallback, useState } from 'react'
import type { StyleClasses } from '@paintscout/ui/styles'
import { useIsMobile } from '@paintscout/ui/hooks'
import Avatar from '@paintscout/ui/components/Avatar/Avatar'
import Button from '@paintscout/ui/components/Button'
import type { DialogProps } from '@paintscout/ui/components/Dialog'
import Dialog, { DialogActions } from '@paintscout/ui/components/Dialog'
import Grid from '@paintscout/ui/components/Grid'
import Skeleton from '@paintscout/ui/components/Skeleton'
import Tab from '@paintscout/ui/components/Tab'
import Table, { TableBody, TableCell, TableHead, TableRow } from '@paintscout/ui/components/Table'
import Tabs from '@paintscout/ui/components/Tabs'
import Tooltip from '@paintscout/ui/components/Tooltip'
import Typography from '@paintscout/ui/components/Typography'
import { LeaderboardConfirmationDialog } from '@paintscout/ui/dialogs/LeaderboardConfirmationDialog'
import DatePickerPresetChips from '../components/DatePicker/DatePickerPresetChips'
import { useClientOptions } from '../providers/ClientOptionsProvider'
import { useDialogs } from '../providers/DialogStack'
import { useUser } from '../providers/UserProvider'
import { useUserPermissions } from '../hooks/useUserPermissions'

export interface LeaderboardDialogProps extends DialogProps {
  classes?: StyleClasses<typeof useStyles>
  onClose?: () => void
  userId?: string
  companyId?: string
  role?: string
  sort: 'totalSold' | 'winRate'
}

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    content: {
      padding: theme.spacing(),
      overflow: 'none'
    },
    minHeight: {
      minHeight: '470px'
    },
    dialogTitle: {
      marginTop: theme.spacing(),
      fontSize: '2.5rem',
      textAlign: 'center',
      fontWeight: 500,
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem'
      }
    },
    dialogSubtitle: {
      textAlign: 'center'
    },
    titleHr: {
      width: '100%',
      height: '4px',
      border: 'none',
      backgroundColor: theme.paintscout.palette.primary
    },
    tableWrapper: {
      marginTop: theme.spacing(3),
      borderRadius: theme.borderRadius.md,
      boxShadow: 'none'
    },
    tableBody: {},
    tableRow: {
      padding: theme.spacing(2)
    },
    tableHeader: {
      backgroundColor: theme.paintscout.palette.primary,
      color: theme.palette.common.white,
      '-webkit-border-radius': theme.spacing(3),
      '-moz-border-radius': theme.spacing(3),
      'border-radius': '5px',
      position: 'sticky',
      zIndex: 10,
      '-webkit-box-shadow': `0px 0px 3px 0px ${theme.paintscout.palette.brand.Neutral.P75}`,
      '-moz-box-shadow': `0px 0px 3px 0px ${theme.paintscout.palette.brand.Neutral.P75}`,
      'box-shadow': `0px 0px 3px 0px ${theme.paintscout.palette.brand.Neutral.P75}`,
      '& div': {
        fontWeight: 'bold',
        backgroundColor: theme.paintscout.palette.primary,
        border: 'none'
      },
      [theme.breakpoints.down('xs')]: {
        '& div': {
          paddingBottom: theme.spacing(1.5),
          paddingTop: theme.spacing(1.75)
        }
      }
    },
    tableHeaderRow: {
      backgroundColor: theme.paintscout.palette.primary
    },
    skeletonBody: {},
    highlightHeaderCell: {
      backgroundColor: theme.paintscout.palette.dark
    },
    highlightHeaderBody: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96
    },
    userBox: {
      zIndex: 3,
      border: 'none',
      background: theme.paintscout.palette.primary,
      color: theme.palette.common.white
    },
    lastUpdated: {
      textAlign: 'center',
      margin: theme.spacing(2, 0, 1),
      color: theme.paintscout.palette.brand.Neutral.P70
    },
    beforeUserBox: {
      borderBottom: 'none'
    },
    businessTypeDiv: {
      display: 'flex',
      justifyContent: 'flex-start',
      marginLeft: '-8px'
    },
    avatar: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        marginRight: theme.spacing(0)
      },
      '& img': {
        fontSize: '1.5em',
        width: '1em',
        height: '1em',
        marginRight: '1rem'
      }
    },
    rankCell: {
      position: 'relative',
      width: '90px',
      [theme.breakpoints.down('sm')]: {
        width: '60px'
      }
    },
    rankCellLine: {
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '3px',
        width: '4px',
        height: '80%',
        backgroundColor: theme.palette.common.white,
        top: '50%',
        transform: 'translateY(-50%)'
      }
    },
    trophyIcon: {
      color: theme.palette.common.white,
      backgroundColor: theme.paintscout.palette.primary,
      borderRadius: '50%',
      padding: theme.spacing(0.25),
      height: '25px',
      width: '25px',
      [theme.breakpoints.down('sm')]: {
        height: '23px',
        width: '23px'
      },
      [theme.breakpoints.down('xs')]: {
        height: '20px',
        width: '20px'
      }
    },
    whiteTrophy: {
      backgroundColor: theme.palette.common.white,
      color: theme.paintscout.palette.primary
    },
    trophyIconPosition: {
      position: 'absolute',
      left: theme.spacing(),
      top: '10px',
      [theme.breakpoints.down('xs')]: {
        top: '12px'
      }
    },
    top10TrophyIconPosition: {
      marginRight: theme.spacing()
    },
    expandMore: {
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(0.25),
      height: '25px',
      width: '25px',
      transform: 'rotate(0deg)',
      transition: 'transform 0.3s ease-in-out'
    },
    inverted: {
      transform: 'rotate(180deg)'
    },
    businessTypeText: {
      padding: '2px 4px',
      margin: '0 4px',
      border: `2px solid ${theme.paintscout.palette.brand.Neutral.P94}`
    },
    businessTypeTextAfterUser: {
      border: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    infoDivRow: {
      position: 'absolute',
      top: theme.spacing(8),
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: `${theme.spacing(4)}px ${theme.spacing(3)}px`,
      paddingTop: theme.spacing(5),
      backgroundColor: theme.palette.common.white,
      textAlign: 'center',
      [theme.breakpoints.down('xs')]: {
        top: theme.spacing(6.5)
      }
    },
    top10Row: {
      position: 'absolute',
      top: theme.spacing(8),
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P94}`,
      height: '47px',
      padding: 0,
      backgroundColor: theme.palette.common.white,
      [theme.breakpoints.down('xs')]: {
        top: theme.spacing(6.5)
      }
    },
    top10Div: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 0,
      backgroundColor: theme.palette.common.white
    },
    top10TextDiv: {
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      margin: `${-theme.spacing()}px ${theme.spacing()}px`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing()
    },
    top10TextDivHover: {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94
      }
    },
    top10Cell: {
      padding: '18px 0px 18px 1px',
      width: '100%',
      cursor: 'pointer',
      backgroundColor: theme.palette.common.white,
      position: 'sticky',
      [theme.breakpoints.down('sm')]: {
        paddingLeft: '2px'
      },
      [theme.breakpoints.down('xs')]: {
        paddingLeft: '3px'
      }
    },
    spacerCell: {
      height: '65px',
      padding: 0,
      margin: 0,
      [theme.breakpoints.down('xs')]: {
        height: '60px'
      }
    },
    spacerRow: {
      borderBottom: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    secondarySpacer: {
      height: '55px'
    },
    userSelectDiv: {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      marginBottom: -theme.spacing(1),
      marginTop: theme.spacing(2),
      '& input': {
        paddingLeft: theme.spacing(0.5)
      },
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'center'
      },
      '& #dropdown-menu-paper': {
        minWidth: '300px',
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
          minWidth: '200px'
        }
      },
      '& #dropdown-menu-paper h6': {
        textTransform: 'none'
      }
    },
    dividerDiv: {
      height: '17px',
      width: '50px',
      borderLeft: `2px solid ${theme.paintscout.palette.brand.Neutral.P60}`,
      marginRight: -theme.spacing(3),
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(2.5)
    },
    hidden: {
      display: 'none'
    },
    helpIcon: {
      color: theme.paintscout.palette.primary,
      fontSize: '0.7rem',
      marginTop: -theme.spacing(),
      marginLeft: theme.spacing(),
      [theme.breakpoints.down('sm')]: {
        marginLeft: theme.spacing(0.5)
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: theme.spacing(1.5)
      }
    },
    highlightedHelpIcon: {
      color: theme.palette.common.white
    },
    confirmButton: {
      background: theme.paintscout.palette.primary,
      color: theme.palette.common.white,
      '&:hover': {
        background: theme.paintscout.palette.primary
      }
    },
    tabsBody: {
      marginLeft: theme.spacing(0.5),
      marginBottom: -theme.spacing(2)
    },
    tab: {
      minHeight: theme.spacing(6),
      color: theme.paintscout.palette.brand.Neutral.P30,
      '&:hover': {
        color: theme.paintscout.palette.dark
      }
    },
    selectedTab: {
      color: theme.paintscout.palette.primary
    },
    tabIndicator: {
      backgroundColor: theme.paintscout.palette.primary
    },
    link: {
      textDecoration: 'none',
      fontWeight: 'bold',
      color: theme.paintscout.palette.primary,
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    additionalInfoDiv: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%'
    },
    datePickerChips: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(2),
      paddingBottom: 0,
      borderBottom: 'none',
      [theme.breakpoints.down('xs')]: {
        paddingLeft: 0,
        paddingRight: 0
      }
    },
    chipWrapper: {
      marginBottom: theme.spacing(3)
    }
  }),
  { name: 'LeaderboardDialog' }
)

function LeaderboardDialog(props: LeaderboardDialogProps) {
  const classes = useStyles(props)
  const { onClose, companyId, userId: propsUserId, role, sort, ...otherProps } = props
  const { openDialog, dismissAllDialogs } = useDialogs()
  const { isSuperadmin, user } = useUser()
  const { permissionToReadAllDocuments } = useUserPermissions()
  let showAdminView = false
  if (permissionToReadAllDocuments && !isSuperadmin) {
    showAdminView = true
  } else if (isSuperadmin && role === 'admin') {
    showAdminView = true
  } else if (isSuperadmin && !role) {
    showAdminView = true
  }
  const [tab, setTab] = useState<'team' | 'self'>('self')
  const searchTeam = tab === 'team'
  const [loading, setLoading] = useState(true)

  const [dateInterval, setDateInterval] = useState('thisMonth')
  const dateIntervalOptions = [
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'lastYear', label: 'Last Year' }
  ]

  const userId = propsUserId ?? user.user_id
  const isXs = useIsMobile({ xs: true })
  const salesLeaderboard = sort === 'totalSold'

  const intervalLength = dateInterval === 'lastYear' ? 'year' : 'month'
  const intervalStart =
    dateInterval === 'thisMonth'
      ? moment().tz('GMT').startOf(intervalLength).unix() * 1000
      : moment().tz('GMT').startOf(intervalLength).subtract(1, intervalLength).unix() * 1000

  const variables = {
    userId,
    companyId,
    sort,
    months: 1,
    searchTeam,
    intervalLength,
    intervalStart
  }

  const { data } = useQuery<
    any,
    { userId: string; companyId: string; sort: string; months: number; searchTeam: boolean }
  >(
    gql`
      query leaderboard(
        $userId: String!
        $companyId: String
        $sort: String!
        $intervalLength: String
        $intervalStart: Float
        $searchTeam: Boolean!
      ) {
        leaderboardData(
          userId: $userId
          companyId: $companyId
          sort: $sort
          intervalLength: $intervalLength
          intervalStart: $intervalStart
          searchTeam: $searchTeam
        ) {
          totalRows
          userRank
          userRows {
            rank
            name
            highlight
            trophy
            winRate
            totalSold
            totalSoldComment
            winRateComment
            numberSold
            businessType
            customAvatar
            playerName
          }
          topRows {
            rank
            name
            highlight
            winRate
            totalSold
            numberSold
            businessType
            customAvatar
            playerName
          }
        }
        leaderboardRank(userId: $userId, intervalLength: $intervalLength, intervalStart: $intervalStart) {
          updatedDate
          totalSold {
            value
            rank
            valueChange
            rankChange
            comment
          }
          winRate {
            value
            rank
            valueChange
            rankChange
            comment
          }
        }
      }
    `,
    {
      skip: false,
      fetchPolicy: 'network-only',
      variables,
      notifyOnNetworkStatusChange: true,
      onError: useCallback((_error) => {
        openDialog(LeaderboardConfirmationDialog, {
          title: 'Whoops!',
          message: `Looks like something’s off on our end. We’re working to get the leaderboards running smoothly in no time. Thanks for your patience!`,
          onConfirm: () => {
            dismissAllDialogs()
          }
        })
      }, []),
      onCompleted: (_data) => {
        setLoading(false)
      }
    }
  )

  const currentRank = data?.leaderboardRank?.[sort]?.rank ?? 1000 // Keep this 1000 in sync w/ check on currentUserRank
  const updatedDate = data?.leaderboardRank?.updatedDate
  const isTop10 = currentRank <= 10
  const [showTop10, setShowTop10] = useState(false)
  const hideTop10Div = loading || searchTeam || data?.leaderboardData?.userRows?.length === 1
  const showInfoDiv = !loading && data?.leaderboardData?.userRows?.length === 1

  function getDateDisplay() {
    const formatDateRange = (startDate: Date, endDate: Date) => {
      const options = { month: 'short', day: 'numeric', year: 'numeric' } as const
      return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`
    }

    let dateDisplay = ''

    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth()

    if (dateInterval === 'thisMonth') {
      dateDisplay = formatDateRange(new Date(year, month, 1), now)
    } else if (dateInterval === 'lastMonth') {
      dateDisplay = formatDateRange(new Date(year, month - 1, 1), new Date(year, month, 0))
    } else {
      dateDisplay = formatDateRange(new Date(year - 1, 0, 1), new Date(year - 1, 11, 31))
    }

    return dateDisplay
  }

  function getCheckBackTime() {
    const now = new Date().getDate()
    const daysUntilRankingsCalculated = 7 - now
    if (daysUntilRankingsCalculated <= 1) {
      return 'Check back tomorrow!'
    }
    return `Check back in ${daysUntilRankingsCalculated} days!`
  }

  return (
    <Dialog open {...otherProps} className={classes.root}>
      <DialogContent className={classNames({ [classes.content]: true, [classes.minHeight]: showInfoDiv })}>
        <Typography variant={'h1'} className={classes.dialogTitle}>
          {salesLeaderboard ? 'Sales' : 'Win Rate'} Leaderboard
        </Typography>
        <Typography variant={'body1'} className={classes.dialogSubtitle}>
          {getDateDisplay()}
        </Typography>
        <div className={classes.chipWrapper}>
          <DatePickerPresetChips
            selectedPreset={dateIntervalOptions.find((option) => option.value === dateInterval)}
            presets={dateIntervalOptions}
            onPresetSelect={(preset) => {
              if (dateInterval === preset.value) return
              setDateInterval(preset.value)
              setShowTop10(false)
              setLoading(true)
            }}
            hideTitle
            classes={{
              root: classes.datePickerChips
            }}
          />
        </div>
        {showAdminView && (
          <div>
            <Tabs
              value={tab ?? 'all'}
              classes={{ root: classes.tabsBody, indicator: classes.tabIndicator }}
              onChange={(ev, newTab) => {
                setTab(newTab)
                setShowTop10(false)
                setLoading(true)
              }}
            >
              <Tab classes={{ root: classes.tab, selected: classes.selectedTab }} value="self" label="Mine" />
              <div className={classes.dividerDiv} />
              <Tab classes={{ root: classes.tab, selected: classes.selectedTab }} value="team" label="Team" />
            </Tabs>
          </div>
        )}
        <div style={{ position: 'relative', width: '100%' }}>
          {/* Top 10 dropdown */}
          <div
            className={classNames({ [classes.top10Row]: true, [classes.hidden]: hideTop10Div })}
            onClick={() => (isTop10 ? null : setShowTop10((prev) => !prev))}
          >
            <div className={classNames({ [classes.top10Cell]: true, [classes.hidden]: hideTop10Div })}>
              <div className={classes.top10Div}>
                <hr className={classes.titleHr} />
                <div className={classNames({ [classes.top10TextDiv]: true, [classes.top10TextDivHover]: !isTop10 })}>
                  <EmojiEventsIcon
                    className={classNames({
                      [classes.trophyIcon]: true,
                      [classes.top10TrophyIconPosition]: true
                    })}
                  />
                  {isTop10 ? (
                    <Typography weight="bold" variant={'body1'}>
                      Congratulations! You&lsquo;re {currentRank === 1 ? 'in the lead' : 'in the Top 10'}
                    </Typography>
                  ) : (
                    <Typography weight="bold" variant={'body1'}>
                      {showTop10 ? 'Hide' : 'View'} Top 10 Ranked Players
                    </Typography>
                  )}
                  {!isTop10 && (
                    <ExpandMore className={classNames({ [classes.expandMore]: true, [classes.inverted]: showTop10 })} />
                  )}
                </div>
                <hr className={classes.titleHr} />
              </div>
            </div>
          </div>
          {/* Info dropdown */}
          <div
            className={classNames({ [classes.infoDivRow]: true, [classes.hidden]: !showInfoDiv })}
            onClick={() => setShowTop10((prev) => !prev)}
          >
            <div className={classNames({ [classes.infoDivCell]: true, [classes.hidden]: !showInfoDiv })}>
              <div className={classes.additionalInfoDiv}>
                <Typography variant="h4">
                  {searchTeam ? `Want to track your team's performance?` : 'Rankings are being calculated!'}
                </Typography>
                <br />
                <Typography variant="body1">
                  {searchTeam ? (
                    <span>
                      <a href="https://app.paintscout.com/settings?section=billing-users" className={classes.link}>
                        <strong>Add a team member</strong>
                      </a>{' '}
                      to get started!
                    </span>
                  ) : (
                    `It's too soon in the month for us to generate rankings. ${getCheckBackTime()}`
                  )}
                </Typography>
              </div>
            </div>
          </div>
          <Card className={classes.tableWrapper}>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow className={classes.tableHeaderRow}>
                  <TableCell align={'center'}>Rank</TableCell>
                  {!isXs && <TableCell align={'center'}></TableCell>}
                  <TableCell align={isXs ? 'center' : 'left'}>Player</TableCell>
                  {/* {!isMobile && <TableCell align={'left'}>Business Type</TableCell>} */}
                  {salesLeaderboard ? (
                    <>
                      <TableCell className={classes.highlightHeaderCell} align={'center'}>
                        Sales
                      </TableCell>
                      <TableCell nowrap noChildPadding align={'center'}>
                        Win Rate
                      </TableCell>
                      <TableCell nowrap noChildPadding align={'center'}>
                        # Sold
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell nowrap className={classes.highlightHeaderCell} align={'center'}>
                        Win Rate
                      </TableCell>
                      <TableCell nowrap noChildPadding align={'center'}>
                        # Sold
                      </TableCell>
                      <TableCell align={'center'}>Sales</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody className={classes.tableBody}>
                {searchTeam ? (
                  <DisplayRows
                    loading={loading}
                    intervalLength={intervalLength}
                    showSpacer={showInfoDiv}
                    rows={data?.leaderboardData?.userRows ?? []}
                    currentRank={currentRank}
                    sort={sort}
                    searchTeam
                  />
                ) : (
                  <>
                    {showTop10 && (
                      <DisplayRows
                        loading={loading}
                        intervalLength={intervalLength}
                        showSpacer={currentRank > 10}
                        rows={data?.leaderboardData?.topRows ?? []}
                        currentRank={currentRank}
                        sort={sort}
                      />
                    )}
                    <DisplayRows
                      loading={loading}
                      intervalLength={intervalLength}
                      rows={data?.leaderboardData?.userRows ?? []}
                      currentRank={currentRank}
                      sort={sort}
                      showSpacer={!hideTop10Div || showInfoDiv}
                    />
                  </>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>

        <Typography variant="body1" className={classes.lastUpdated}>
          Last updated {moment(updatedDate).format('MMM D [at] h:mma')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent={'space-between'}>
          <Grid item>
            <Button
              onClick={() => {
                window.open('http://help.paintscout.com/en/articles/10375453-what-are-the-paintscout-leaderboards')
              }}
              variant={'text'}
            >
              Learn More
            </Button>
          </Grid>
          <Grid item>
            <Button className={classes.confirmButton} onClick={onClose} variant={'contained'}>
              Close
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

export default LeaderboardDialog

function DisplayRows({
  loading,
  rows,
  currentRank,
  intervalLength,
  sort,
  showSpacer,
  searchTeam
}: {
  loading: boolean
  rows: {
    rank
    name
    winRate
    totalSold
    businessType
  }[]
  currentRank: number
  intervalLength?: string
  sort: string
  showSpacer?: boolean
  searchTeam?: boolean
}) {
  const classes = useStyles({})
  const isMobile = useIsMobile()
  const isXs = useIsMobile({ xs: true })
  const theme = useTheme()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })

  if (loading) {
    return (
      <>
        {showSpacer &&
          Array.from({ length: 4 }).map((_, index) => (
            <TableRow borderWidth={0} key={index} className={classes.skeletonBody}>
              <TableCell className={classes.shimCell}></TableCell>
            </TableRow>
          ))}
        {Array.from({ length: 10 }).map((_, index) => (
          <TableRow borderWidth={2} key={index} className={classes.skeletonBody}>
            <TableCell className={classes.shimCell}>
              <Skeleton width="100%" height={'16px'} color={theme.paintscout.palette.brand.Neutral.P94} />
            </TableCell>
            <TableCell className={classes.shimCell}>
              <Skeleton width="100%" height={'16px'} color={theme.paintscout.palette.brand.Neutral.P94} />
            </TableCell>
            <TableCell className={classes.shimCell}>
              <Skeleton width="100%" height={'16px'} color={theme.paintscout.palette.brand.Neutral.P94} />
            </TableCell>
            <TableCell className={classes.shimCell}>
              <Skeleton width="100%" height={'16px'} color={theme.paintscout.palette.brand.Neutral.P94} />
            </TableCell>
            {!isXs && (
              <TableCell className={classes.shimCell}>
                <Skeleton width="100%" height={'16px'} color={theme.paintscout.palette.brand.Neutral.P94} />
              </TableCell>
            )}
            <TableCell className={classes.shimCell}>
              <Skeleton width="100%" height={'16px'} color={theme.paintscout.palette.brand.Neutral.P94} />
            </TableCell>
          </TableRow>
        ))}
      </>
    )
  }
  const multiRow = rows.length != 1

  return (
    <>
      {showSpacer &&
        Array.from({ length: multiRow ? 1 : 3 }).map((_, index) => (
          <TableRow
            key={`filler-${index}`} // Filler row to account for dropdown header
            noBorder
            className={classNames({ [classes.spacerRow]: multiRow })}
          >
            <TableCell
              className={classNames({ [classes.spacerCell]: true, [classes.secondarySpacer]: index > 0 })}
              style={{ paddingLeft: 0 }}
              align={'center'}
            >
              {multiRow && <hr className={classes.titleHr} />}
            </TableCell>
            <TableCell
              className={classNames({ [classes.spacerCell]: true, [classes.secondarySpacer]: index > 0 })}
              align={'center'}
            >
              {multiRow && <hr className={classes.titleHr} />}
            </TableCell>
            <TableCell
              className={classNames({ [classes.spacerCell]: true, [classes.secondarySpacer]: index > 0 })}
              align={'center'}
            >
              {multiRow && <hr className={classes.titleHr} />}
            </TableCell>
            <TableCell
              className={classNames({ [classes.spacerCell]: true, [classes.secondarySpacer]: index > 0 })}
              align={'center'}
            >
              {multiRow && <hr className={classes.titleHr} />}
            </TableCell>
            <TableCell
              className={classNames({ [classes.spacerCell]: true, [classes.secondarySpacer]: index > 0 })}
              align={'center'}
            >
              {multiRow && <hr className={classes.titleHr} />}
            </TableCell>
            <TableCell
              className={classNames({ [classes.spacerCell]: true, [classes.secondarySpacer]: index > 0 })}
              align={'center'}
            >
              {multiRow && <hr className={classes.titleHr} />}
            </TableCell>
          </TableRow>
        ))}
      <>
        {rows?.map((row: any, index: number) => {
          const currentUserRank = currentRank === row.rank || currentRank === 1000 // Keep this 1000 in sync w/ check fallback on currentRank
          const highlight = row.highlight
          const showTrophy = row.trophy
          const beforeUserBox = index > 0 && (rows[index + 1] as any)?.highlight && !searchTeam
          const showUserBox = searchTeam ? rows.length === 1 : currentUserRank && highlight

          const boldName = highlight && (!searchTeam || rows.length === 1)

          let comment = sort === 'winRate' ? row.winRateComment : row.totalSoldComment
          if (comment === 'excluded') {
            comment = (
              <>
                Our algorithm does not have you on file. <a href="mailto:support@paintscout.com">Contact us</a> for more
                details.
              </>
            )
          } else if ((comment === 'not ranked' || !comment) && intervalLength === 'month') {
            comment = 'Not ranked in the top 300 players'
          } else if ((comment === 'not ranked' || !comment) && intervalLength === 'year') {
            comment = 'Not ranked in the top 50 players'
          } else if (comment === 'less than 10 quotes sent') {
            comment = `Has not sent 10 ${objectLabels.quote.plural.toLowerCase()}.`
          } else if (comment === 'less than 50 quotes sent') {
            comment = `Has not sent 50 ${objectLabels.quote.plural.toLowerCase()}.`
          }
          const totalSold = formatCurrency({ options, value: row.totalSold, abbreviationPostfix: '' })
          const winRate = row?.winRate ? `${row.winRate.toFixed(2)}%` : '0%'
          const numberSold = row?.numberSold || '0'

          return (
            <TableRow
              className={classNames({
                [classes.userBox]: showUserBox,
                [classes.beforeUserBox]: beforeUserBox,
                [classes.tableRow]: true
              })}
              key={index}
              borderWidth={2}
            >
              <TableCell
                className={classNames({ [classes.rankCell]: true, [classes.rankCellLine]: showUserBox && !showTrophy })}
                align={'center'}
              >
                {showTrophy && (
                  <EmojiEventsIcon
                    className={classNames({
                      [classes.trophyIcon]: true,
                      [classes.trophyIconPosition]: true,
                      [classes.whiteTrophy]: showUserBox
                    })}
                  />
                )}
                {!row.rank ? (
                  <Tooltip
                    icon={
                      <Help
                        fontSize="small"
                        className={classNames({
                          [classes.helpIcon]: true,
                          [classes.highlightedHelpIcon]: showUserBox
                        })}
                      />
                    }
                    interactive
                    content={comment}
                    placement={isMobile ? 'bottom-start' : 'bottom'}
                  >
                    <Typography variant="body1" weight={'bold'} color={showUserBox ? 'initial' : 'textSecondary'}>
                      {'NR'}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="body1" weight={'bold'} color={showUserBox ? 'initial' : 'textSecondary'}>
                    {row.rank}
                  </Typography>
                )}
              </TableCell>
              <TableCell align={isMobile ? 'center' : 'left'}>
                {row.customAvatar ? (
                  <Avatar variant="circle" tooltip={false} className={classes.avatar} src={row.customAvatar} />
                ) : (
                  <Avatar
                    variant="circle"
                    tooltip={row.playerName}
                    avatarColor={showUserBox ? '#FFFFFF' : null}
                    className={classes.avatar}
                  >
                    {row.name}
                  </Avatar>
                )}
              </TableCell>
              {!isXs && (
                <TableCell align={'left'}>
                  <Typography variant="body1" weight={boldName ? 'bold' : 'normal'} style={{ whiteSpace: 'no-wrap' }}>
                    {`${row.name}`}
                  </Typography>
                </TableCell>
              )}
              {sort === 'totalSold' ? (
                <>
                  <TableCell
                    className={classNames({
                      [classes.highlightHeaderBody]: searchTeam ? rows.length > 1 : !highlight
                    })}
                    align={'center'}
                  >
                    <Typography variant="body1" weight={'bold'}>
                      {totalSold}
                    </Typography>
                  </TableCell>
                  <TableCell align={'center'}>
                    <Typography variant="body1" weight={'bold'}>
                      {winRate}
                    </Typography>
                  </TableCell>
                  <TableCell align={'center'}>
                    <Typography variant="body1" weight={'bold'}>
                      {numberSold}
                    </Typography>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell
                    className={classNames({
                      [classes.highlightHeaderBody]: searchTeam ? rows.length > 1 : !highlight
                    })}
                    align={'center'}
                  >
                    <Typography variant="body1" weight={'bold'}>
                      {winRate}
                    </Typography>
                  </TableCell>
                  <TableCell align={'center'}>
                    <Typography variant="body1" weight={'bold'}>
                      {numberSold}
                    </Typography>
                  </TableCell>
                  <TableCell align={'center'}>
                    <Typography variant="body1" weight={'bold'}>
                      {totalSold}
                    </Typography>
                  </TableCell>
                </>
              )}
            </TableRow>
          )
        })}
      </>
    </>
  )
}
